// ./src/utils/api/ParkingSessionService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { Car } from "./CarService";
import { Expense } from "./ExpenseService";
import { Travel } from "./TravelService";
import { UploadedFile } from "./UploadedFileService";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const PARKING_SESSION_FIELDS = `
  id
  accountId
  carId
  travelId
  startTime
  endTime
  durationMinutes
  initialPrice
  finalPrice
  currency
  latitude
  longitude
  formattedAddress
  uploadedFileId
  notes
  expenseId
  startedBy
  endedBy
  status
  version
  createdBy
  updatedBy
  createdAt
  updatedAt
  car {
    id
    label
  }
  travel {
    id
    purpose
    destination
  }
  expense {
    id
    totalPrice
    paidInCurrency
  }
  uploadedFile {
    id
    fileUrl
    thumbnailUrl
  }
  userStarted {
    id
    firstName
    lastName
  }
  userEnded {
    id
    firstName
    lastName
  }
  userCreated {
    id
    firstName
    lastName
  }
  userUpdated {
    id
    firstName
    lastName
  }
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const PARKING_SESSION_LIST_QUERY = gql`
  query ParkingSessionList($filter: ParkingSessionFilter, $params: PaginationAndSorting) {
    parkingSessionList(filter: $filter, params: $params) {
      code
      data {
        ${PARKING_SESSION_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const PARKING_SESSION_GET_QUERY = gql`
  query ParkingSessionGet($id: ID) {
    parkingSessionGet(id: $id) {
      code
      data {
        ${PARKING_SESSION_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const PARKING_SESSION_GET_MANY_QUERY = gql`
  query ParkingSessionGetMany($ids: [ID]) {
    parkingSessionGetMany(ids: $ids) {
      code
      data {
        ${PARKING_SESSION_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const PARKING_SESSION_CREATE_MUTATION = gql`
  mutation ParkingSessionCreate($params: ParkingSessionInput) {
    parkingSessionCreate(params: $params) {
      code
      data {
        ${PARKING_SESSION_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const PARKING_SESSION_UPDATE_MUTATION = gql`
  mutation ParkingSessionUpdate($where: ParkingSessionWhereInput, $params: ParkingSessionInput) {
    parkingSessionUpdate(where: $where, params: $params) {
      code
      data {
        ${PARKING_SESSION_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const PARKING_SESSION_REMOVE_MUTATION = gql`
  mutation ParkingSessionRemove($where: ParkingSessionWhereInput) {
    parkingSessionRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

/**
 * Parking session status constants
 */
export const PARKING_SESSION_STATUS = {
  ACTIVE: 100,
  COMPLETED: 200,
} as const;

export type ParkingSessionStatusValue = (typeof PARKING_SESSION_STATUS)[keyof typeof PARKING_SESSION_STATUS];

export interface ParkingSession {
  id?: string | undefined;
  accountId?: string | undefined;

  /** Vehicle this parking session belongs to */
  carId?: string | undefined;

  /** Associated travel record (if parked during a trip) */
  travelId?: string | undefined;

  // Timing
  /** When parking started (UTC) */
  startTime?: string | undefined;
  /** When parking ended, null while active (UTC) */
  endTime?: string | undefined;
  /** Paid duration in minutes (null = free or unknown duration) */
  durationMinutes?: number | undefined;

  // Pricing
  /** Price entered at start */
  initialPrice?: number | undefined;
  /** Price confirmed at end */
  finalPrice?: number | undefined;
  /** Currency code: USD, CAD, EUR, etc. */
  currency?: string | undefined;

  // Location
  /** GPS latitude of parking location */
  latitude?: number | undefined;
  /** GPS longitude of parking location */
  longitude?: number | undefined;
  /** Reverse-geocoded or manually entered address */
  formattedAddress?: string | undefined;

  // Attachments & notes
  /** Reference to a photo from the parking spot */
  uploadedFileId?: string | undefined;
  uploadedFile?: UploadedFile | null | undefined;
  /** User notes (e.g., Level 3, spot B12) */
  notes?: string | undefined;

  // Linked expense
  /** Reference to the auto-generated expense (set on close when finalPrice > 0) */
  expenseId?: string | undefined;

  // User tracking
  /** User who started the session */
  startedBy?: string | undefined;
  /** User who ended the session */
  endedBy?: string | undefined;

  // Status and audit fields
  /**
   * Session status:
   * - 100: Active (parking in progress)
   * - 200: Completed (session ended)
   */
  status?: ParkingSessionStatusValue | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;

  // Resolved references
  car?: Car | undefined;
  travel?: Travel | undefined;
  /** The auto-generated parking expense (if finalPrice > 0) */
  expense?: Expense | undefined;
  /** User who started the session */
  userStarted?: User | undefined;
  /** User who ended the session */
  userEnded?: User | undefined;
  userCreated?: User | undefined;
  userUpdated?: User | undefined;
}

export interface ParkingSessionInput {
  id?: string | null | undefined;

  /** Vehicle this parking session belongs to */
  carId?: string | null | undefined;
  /** Associated travel record */
  travelId?: string | null | undefined;

  // Timing
  /** When parking started (UTC) */
  startTime?: string | null | undefined;
  /** When parking ended (UTC) */
  endTime?: string | null | undefined;
  /** Paid duration in minutes (null = free or unknown) */
  durationMinutes?: number | null | undefined;

  // Pricing
  /** Price entered at start */
  initialPrice?: number | null | undefined;
  /** Price confirmed at end (0 or null = free parking, no expense created) */
  finalPrice?: number | null | undefined;
  /** Currency code (defaults to vehicle/account home currency) */
  currency?: string | null | undefined;

  // Location
  /** GPS latitude */
  latitude?: number | null | undefined;
  /** GPS longitude */
  longitude?: number | null | undefined;
  /** Address text */
  formattedAddress?: string | null | undefined;

  // Attachments & notes
  /** Photo of parking spot */
  uploadedFileId?: string | null | undefined;
  /** Notes (e.g., Level 3, spot B12) */
  notes?: string | null | undefined;

  /** Session status: 100=Active, 200=Completed */
  status?: ParkingSessionStatusValue | null | undefined;
}

export interface ParkingSessionFilter {
  id?: string[] | null | undefined;
  accountId?: string[] | null | undefined;
  carId?: string[] | null | undefined;
  travelId?: string[] | null | undefined;
  expenseId?: string[] | null | undefined;
  startedBy?: string[] | null | undefined;
  endedBy?: string[] | null | undefined;
  /** Filter by status: 100=Active, 200=Completed */
  status?: ParkingSessionStatusValue[] | null | undefined;
  /** Filter sessions starting on or after this date */
  startTimeFrom?: string | null | undefined;
  /** Filter sessions starting on or before this date */
  startTimeTo?: string | null | undefined;
  /** Filter sessions ending on or after this date */
  endTimeFrom?: string | null | undefined;
  /** Filter sessions ending on or before this date */
  endTimeTo?: string | null | undefined;
  /** Search in address and notes */
  searchKeyword?: string | null | undefined;
}

export interface ParkingSessionWhereInput {
  id?: string | undefined;
}

export interface ParkingSessionEndProps {
  endTime?: string | null | undefined;
  finalPrice?: number | null | undefined;
  currency?: string | null | undefined;
  notes?: string | null | undefined;
  startTime?: string | null | undefined;
  travelId?: string | null | undefined;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Check if a parking session is currently active
 */
export function isSessionActive(session: ParkingSession): boolean {
  return session.status === PARKING_SESSION_STATUS.ACTIVE;
}

/**
 * Check if a paid parking session has expired (duration exceeded)
 */
export function isSessionExpired(session: ParkingSession): boolean {
  if (
    session.status !== PARKING_SESSION_STATUS.ACTIVE ||
    !session.startTime ||
    !session.durationMinutes
  ) {
    return false;
  }

  const startTime = new Date(session.startTime);
  const expiresAt = new Date(startTime.getTime() + session.durationMinutes * 60 * 1000);
  return new Date() > expiresAt;
}

/**
 * Get the remaining time in minutes for an active paid session.
 * Returns negative value if expired, null if no duration set.
 */
export function getSessionRemainingMinutes(session: ParkingSession): number | null {
  if (!session.startTime || !session.durationMinutes) {
    return null;
  }

  const startTime = new Date(session.startTime);
  const expiresAt = new Date(startTime.getTime() + session.durationMinutes * 60 * 1000);
  const remainingMs = expiresAt.getTime() - Date.now();
  return Math.round(remainingMs / 60000);
}

/**
 * Get the elapsed time in minutes since parking started
 */
export function getSessionElapsedMinutes(session: ParkingSession): number | null {
  if (!session.startTime) {
    return null;
  }

  const startTime = new Date(session.startTime);
  const endTime = session.endTime ? new Date(session.endTime) : new Date();
  const elapsedMs = endTime.getTime() - startTime.getTime();
  return Math.round(elapsedMs / 60000);
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * Parking session service that handles all parking-related API calls.
 * Supports standard CRUD operations.
 * - Create = start a parking session
 * - Update with status=200 = end a parking session (auto-generates expense if finalPrice > 0)
 */
export class ParkingSessionService {
  /**
   * Get a list of parking sessions with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: ParkingSessionFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(PARKING_SESSION_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single parking session by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(PARKING_SESSION_GET_QUERY, {
      id,
    });
    return result;
  }

  /**
   * Get multiple parking sessions by IDs
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(PARKING_SESSION_GET_MANY_QUERY, {
      ids,
    });
    return result;
  }

  /**
   * Start a new parking session.
   * carId is required. startTime defaults to now if not provided.
   */
  static async start(params: ParkingSessionInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(PARKING_SESSION_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an active parking session (edit location, notes, duration, etc.)
   */
  static async update(
    where: ParkingSessionWhereInput,
    params: ParkingSessionInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(PARKING_SESSION_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * End an active parking session.
   * Sets status to COMPLETED and optionally generates a parking expense.
   * Convenience wrapper around update() with status=200.
   */
  static async end(
    id: string,
    params: ParkingSessionEndProps
  ): Promise<OpResult> {
    return ParkingSessionService.update(
      { id },
      {
        ...params,
        status: PARKING_SESSION_STATUS.COMPLETED,
      }
    );
  }

  /**
   * Remove/delete a parking session (soft delete)
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(PARKING_SESSION_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }
}